import _ from 'lodash';
import classnames from 'classnames';
import { faInfoCircle } from '@fortawesome/pro-light-svg-icons/faInfoCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Assignee from 'models/assignee';
import AssignmentContainer, { RoutingGroupOptionsPropTypes } from 'components/lib/assignment_container';
import InformationIcon from 'components/lib/icons/information_icon';
import Input from 'components/common/input';
import NumericInput from 'components/common/numeric_input';
import Tooltip from 'components/lib/deprecated_tooltip';

const NAME_MAX_LENGTH = 500;

const { t } = useTranslation();

export default class CommunicationQueueEditor extends PureComponent {
  constructor(props) {
    super(props);

    _.bindAll(this, ['editName', 'editSla', 'editInbox']);
  }

  render() {
    return (
      <div id={`communicationQueueEditor-${this.props.id}`} className="communicationQueueEditor">
        {this.renderHeader()}
        <div className="communicationQueueEditor-fields">
          {this.renderAddress()}
          {this.renderName()}
          {this.renderInbox()}
          {this.renderSla()}
        </div>
      </div>
    );
  }

  renderHeader() {
    if (!this.props.renderHeader) {
      return null;
    }
    const slaMessage = 'SLAs in gray represent the Inbox SLA. Setting a shorter SLA here will override the Inbox SLA.';
    return (
      <div className="communicationQueueEditor-header">
        <div className="communicationQueueEditor-header-address">Entry Point</div>
        <div className="communicationQueueEditor-header-name">Name</div>
        <div className="communicationQueueEditor-header-inbox">Inbox</div>
        <div className="communicationQueueEditor-header-sla">
          <span>SLA in minutes</span>
          <Tooltip enabled message={slaMessage} position="bottom">
            <div className="communicationQueueEditor-header-sla-infoWrapper">
              <InformationIcon className="communicationQueueEditor-header-sla-info" />
            </div>
          </Tooltip>
        </div>
      </div>
    );
  }

  renderAddress() {
    return (
      <div className="communicationQueueEditor-addressWrapper">
        <span className="communicationQueueEditor-address">{this.props.address}</span>
      </div>
    );
  }

  renderName() {
    return (
      <Input
        className="communicationQueueEditor-name"
        wrapperClassName="communicationQueueEditor-nameWrapper"
        value={this.props.name || ''}
        placeholder={'Enter name'}
        onChange={this.editName}
        maxLength={NAME_MAX_LENGTH}
      />
    );
  }

  renderSla() {
    let hasLongSla = this.props.slaSeconds > this.props.routingGroupSlaSeconds;
    let classes = classnames('communicationQueueEditor-sla', {
      'communicationQueueEditor-sla-warning': hasLongSla,
    });
    return (
      <Tooltip
        delayed
        enabled={hasLongSla}
        message={t('The Inbox SLA is shorter than the SLA entered. The longer SLA will be used.')}
        position="bottom"
      >
        <div className="communicationQueueEditor-slaWrapper">
          <NumericInput
            className={classes}
            maxValue={60 * 24 * 30 /* minutes per month */}
            minValue={0}
            onSubmit={this.editSla}
            placeholder={(this.props.routingGroupSlaSeconds / 60).toString()}
            value={this.props.slaSeconds && this.props.slaSeconds / 60}
            allowFloat
          />
        </div>
      </Tooltip>
    );
  }

  renderInbox() {
    return (
      <AssignmentContainer
        assignee={new Assignee({ routingGroupId: this.props.routingGroupId })}
        className="communicationQueueEditor-inbox"
        dropDown
        isInline
        onAssigneeChange={this.editInbox}
        onlyGroups
        routingGroupOptions={this.props.routingGroupOptions}
      />
    );
  }

  editName(evt) {
    this.props.editQueue({ id: this.props.id, value: evt.target.value || '', key: 'name' });
  }

  editSla(minutes) {
    let value = minutes ? Math.round(minutes * 60) : minutes;
    this.props.editQueue({ id: this.props.id, value, key: 'slaSeconds' });
  }

  editInbox(attrs) {
    this.props.editQueue({ id: this.props.id, value: attrs.routingGroupId, key: 'routingGroupId' });
  }
}

export const VoiceConfiguration = props => {
  const { editVoiceConfiguration, voiceConfiguration = {} } = props;

  const { callWrapUpIntervalSecs, declineCallAutoReadyIntervalSecs } = voiceConfiguration;

  return (
    <div className="communicationQueueEditor-voiceConfiguration">
      <div className="communicationQueueEditor-voiceConfiguration-container">
        <div className="communicationQueueEditor-voiceConfiguration-header">
          <div className="communicationQueueEditor-header-text">After Call Work (in sec)</div>
          <ConfigurationTooltip message="After a call ends, the number of seconds an agent has until the agent is routed a new call" />
        </div>
        <NumericInput
          className="communicationQueueEditor-voiceConfiguration-input"
          minValue={0}
          onChange={value => editVoiceConfiguration({ key: 'callWrapUpIntervalSecs', value })}
          value={callWrapUpIntervalSecs}
        />
      </div>
      <div className="communicationQueueEditor-voiceConfiguration-container">
        <div className="communicationQueueEditor-voiceConfiguration-header">
          <div className="communicationQueueEditor-header-text">Time to route next call after declining (in sec)</div>
          <ConfigurationTooltip message="After declining a call, the number of seconds until the agent is routed a new call" />
        </div>
        <NumericInput
          className="communicationQueueEditor-voiceConfiguration-input"
          minValue={0}
          onChange={value => editVoiceConfiguration({ key: 'declineCallAutoReadyIntervalSecs', value })}
          value={declineCallAutoReadyIntervalSecs}
        />
      </div>
    </div>
  );
};

const ConfigurationTooltip = ({ message }) => (
  <Tooltip message={message} position="bottom">
    <div>
      <FontAwesomeIcon className="communicationQueueEditor-configuration-tooltip" icon={faInfoCircle} />
    </div>
  </Tooltip>
);

CommunicationQueueEditor.propTypes = {
  id: PropTypes.string,
  address: PropTypes.string,
  editQueue: PropTypes.func.isRequired,
  editVoiceConfiguration: PropTypes.func.isRequired,
  endpointId: PropTypes.string,
  name: PropTypes.string,
  renderHeader: PropTypes.bool,
  routingGroupId: PropTypes.string,
  routingGroupOptions: RoutingGroupOptionsPropTypes,
  routingGroupSlaSeconds: PropTypes.number,
  routingSelector: PropTypes.string,
  slaSeconds: PropTypes.number,
  type: PropTypes.string,
  voiceConfiguration: PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/admin/communication_queue_editor.jsx